import { useEffect, useState } from 'react';
import {
  ExecutivePower,
  GamePhase,
  Vote,
  type GameView,
  type PrivatePlayerState,
  type PublicGameState,
} from '@secretnenshi/shared';
import { HoldToReveal } from '../components/HoldToReveal';
import { Button, Narrator } from '../components/ui';
import { play, showNotice } from '../state/actions';
import { useStore } from '../state/store';
import { useTheme } from '../theme/useTheme';
import { PolicyCard } from './Board';
import { nameOf, type Copy } from './copy';
import { MyIdentity } from './Identity';
import { QuestionPeriod, SpeechFromThrone } from './RoundPhases';

/*
 * The "current action" area (spec §43). What appears here depends only on
 * public facts (the phase and who holds which office), never on anyone's
 * hidden role. Minister and Premier see their policy cards while it is their
 * turn to act, as in spec §31–§32. All themed words come from the variant.
 */

interface Ctx {
  view: GameView;
  game: PublicGameState;
  me: string;
  priv: PrivatePlayerState | null;
  busy: boolean;
  c: Copy;
}

export function ActionPanel({ view }: { view: GameView }) {
  const me = useStore((s) => s.session?.playerId) ?? '';
  const priv = useStore((s) => s.priv);
  const busy = useStore((s) => s.busy);
  const c = useTheme();
  const game = view.game!;
  const ctx: Ctx = { view, game, me, priv, busy, c };
  const self = game.players.find((p) => p.id === me);

  const isHost = view.hostPlayerId === me;
  if (game.phase === GamePhase.SPEECH_FROM_THRONE) return <SpeechFromThrone {...ctx} isHost={isHost} />;
  if (game.phase === GamePhase.QUESTION_PERIOD) return <QuestionPeriod {...ctx} isHost={isHost} />;

  if (self && !self.active && game.phase !== GamePhase.ROLE_REVEAL) {
    return (
      <Panel title={c.t.phases.removed.title}>
        <p>You have been removed from active play. You can still watch, and hold My identity to see your role.</p>
        <Narrator>{c.t.phases.removed.narrator}</Narrator>
      </Panel>
    );
  }

  switch (game.phase) {
    case GamePhase.ROLE_REVEAL:
      return <RoleReveal {...ctx} />;
    case GamePhase.NOMINATE_PREMIER:
      return <Nominate {...ctx} />;
    case GamePhase.CONFIDENCE_VOTE:
      return <ConfidenceVote {...ctx} />;
    case GamePhase.VOTE_RESULT:
      return <VoteResult {...ctx} />;
    case GamePhase.MINISTER_POLICY:
      return <MinisterPolicy {...ctx} />;
    case GamePhase.PREMIER_POLICY:
      return <PremierPolicy {...ctx} />;
    case GamePhase.VETO_RESPONSE:
      return <VetoResponse {...ctx} />;
    case GamePhase.POLICY_RESULT:
      return <PolicyResult {...ctx} />;
    case GamePhase.EXECUTIVE_POWER:
      return <Power {...ctx} />;
    default:
      return null;
  }
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="action" aria-live="polite">
      <h2 className="action-title">{title}</h2>
      {children}
    </section>
  );
}

function Waiting({ text }: { text: string }) {
  return <p className="waiting">{text}</p>;
}

/** Choose one player, then confirm. Accidental taps shouldn't govern anything. */
function PlayerPicker({ view, ids, action, onConfirm, busy }: { view: GameView; ids: string[]; action: string; onConfirm: (id: string) => void; busy: boolean }) {
  const [choice, setChoice] = useState<string | null>(null);
  useEffect(() => {
    setChoice(null);
  }, [ids.join()]);
  return (
    <div className="picker">
      <div className="choices" role="radiogroup" aria-label={action}>
        {ids.map((id) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={choice === id}
            className={`choice ${choice === id ? 'selected' : ''}`}
            onClick={() => setChoice(id)}
          >
            {nameOf(view, id)}
          </button>
        ))}
      </div>
      <Button disabled={!choice || busy} onClick={() => choice && onConfirm(choice)}>
        {choice ? `${action}: ${nameOf(view, choice)}` : action}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function RoleReveal({ view, game, me, busy }: Ctx) {
  const ready = game.roleAcknowledgedPlayerIds.includes(me);
  const isHost = view.hostPlayerId === me;
  const waitingOn = game.players.length - game.roleAcknowledgedPlayerIds.length;
  return (
    <Panel title="Your identity is ready">
      <Narrator>Make sure nobody is looking.</Narrator>
      <MyIdentity big />
      {ready ? (
        <Waiting text={`Waiting for ${waitingOn} more ${waitingOn === 1 ? 'player' : 'players'}.`} />
      ) : (
        <Button variant="secondary" disabled={busy} onClick={() => void play.acknowledgeRole()}>
          I’ve seen it
        </Button>
      )}
      {isHost ? (
        <Button variant="quiet" disabled={busy} onClick={() => void play.skipReveal()}>
          Start without waiting
        </Button>
      ) : null}
    </Panel>
  );
}

function Nominate({ view, game, me, busy, c }: Ctx) {
  const minister = nameOf(view, game.currentMinisterPlayerId);
  const note = c.eligibilityNote(view);
  const momentum = game.nenshiThresholdReached ? <Narrator>{c.t.leader.momentumText}</Narrator> : null;
  if (game.currentMinisterPlayerId !== me) {
    return (
      <Panel title="Nomination">
        <Waiting text={`Waiting for ${minister} to nominate a ${c.premier}.`} />
        {note ? <p className="term-note">{note}</p> : null}
        {momentum}
      </Panel>
    );
  }
  return (
    <Panel title={`Nominate a ${c.premier}`}>
      <p>
        You are {c.minister}. Choose who will serve as {c.premier}.
      </p>
      {note ? <p className="term-note">{note}</p> : null}
      {momentum}
      <PlayerPicker view={view} ids={game.eligiblePremierPlayerIds} action="Nominate" busy={busy} onConfirm={(id) => void play.nominate(id)} />
    </Panel>
  );
}

/**
 * Secret ballot. The buttons never show which one you chose, so a glance at
 * your phone doesn't give your vote away.
 */
function ConfidenceVote({ view, game, me, busy, c }: Ctx) {
  const v = c.t.phases.confidenceVote;
  const active = game.players.filter((p) => p.active).length;
  const voted = game.votedPlayerIds.includes(me);
  const cast = (vote: Vote) => void play.vote(vote);
  return (
    <Panel title={v.name}>
      <p>
        <strong>{nameOf(view, game.currentMinisterPlayerId)}</strong> nominated <strong>{nameOf(view, game.nominatedPremierPlayerId)}</strong> for{' '}
        {c.premier}.
      </p>
      <Narrator>{v.narrator}</Narrator>
      <div className="row">
        <Button variant="secondary" disabled={busy} onClick={() => cast(Vote.CONFIDENCE)}>
          {v.confidence}
        </Button>
        <Button variant="secondary" disabled={busy} onClick={() => cast(Vote.NO_CONFIDENCE)}>
          {v.noConfidence}
        </Button>
      </div>
      {voted ? <p className="vote-recorded">Your vote is recorded. You can change it until everyone has voted.</p> : null}
      <Waiting text={`${game.votedPlayerIds.length} of ${active} votes in. Ballots are secret; only the totals are announced.`} />
    </Panel>
  );
}

function VoteResult({ game, c }: Ctx) {
  const v = c.t.phases.confidenceVote;
  const r = game.publicVoteResult!;
  return (
    <Panel title={r.passed ? 'Government approved' : 'Government defeated'}>
      <p className="result-line">
        {v.confidence} {r.confidence} – {r.noConfidence} {v.noConfidence}
      </p>
      <Narrator>{r.passed ? v.after : c.t.phases.gridlock.narrator}</Narrator>
    </Panel>
  );
}

function MinisterPolicy({ view, game, me, priv, busy, c }: Ctx) {
  const [choice, setChoice] = useState<number | null>(null);
  if (game.currentMinisterPlayerId !== me) {
    return (
      <Panel title="Legislative session">
        <Waiting text={`${nameOf(view, game.currentMinisterPlayerId)} is reviewing the policy briefing.`} />
      </Panel>
    );
  }
  const hand = priv?.legislativeHand ?? [];
  return (
    <Panel title={c.t.phases.ministerBriefing}>
      <p>You received three policies. Discard one; the other two go to the {c.premier}.</p>
      <div className="hand">
        {hand.map((p, i) => (
          <PolicyCard key={i} policy={p} index={i} selected={choice === i} onSelect={() => setChoice(i)} />
        ))}
      </div>
      <Button disabled={choice === null || busy} onClick={() => choice !== null && void play.discard(choice)}>
        Discard selected
      </Button>
    </Panel>
  );
}

function PremierPolicy({ view, game, me, priv, busy, c }: Ctx) {
  const [choice, setChoice] = useState<number | null>(null);
  const premier = game.nominatedPremierPlayerId;
  if (premier !== me) {
    return (
      <Panel title="Legislative session">
        <Waiting text={`The remaining options have been sent to ${nameOf(view, premier)}.`} />
      </Panel>
    );
  }
  const hand = priv?.legislativeHand ?? [];
  const vetoRefused = game.log.some((l) => l.type === 'VETO_RESOLVED' && !l.accepted && l.turn === game.turnNumber);
  return (
    <Panel title={c.t.phases.premierDesk}>
      <p>The {c.minister} sent two policies. Enact one; the other is discarded.</p>
      <div className="hand">
        {hand.map((p, i) => (
          <PolicyCard key={i} policy={p} index={i} selected={choice === i} onSelect={() => setChoice(i)} />
        ))}
      </div>
      <Button disabled={choice === null || busy} onClick={() => choice !== null && void play.enact(choice)}>
        Enact selected
      </Button>
      {game.vetoUnlocked && !vetoRefused ? (
        <Button variant="secondary" disabled={busy} onClick={() => void play.requestVeto()}>
          Propose {c.t.powers.VETO.name}
        </Button>
      ) : null}
    </Panel>
  );
}

function VetoResponse({ view, game, me, priv, busy, c }: Ctx) {
  const veto = c.t.powers.VETO;
  if (game.currentMinisterPlayerId !== me) {
    return (
      <Panel title={veto.name}>
        <Waiting text={`The ${c.premier} proposed a veto. Waiting for ${nameOf(view, game.currentMinisterPlayerId)}.`} />
        <Narrator>{veto.narrator}</Narrator>
      </Panel>
    );
  }
  return (
    <Panel title={veto.name}>
      <p>The {c.premier} proposes discarding both policies. An accepted veto counts as a failed government.</p>
      <div className="hand">
        {(priv?.legislativeHand ?? []).map((p, i) => (
          <PolicyCard key={i} policy={p} index={i} />
        ))}
      </div>
      <div className="row">
        <Button variant="secondary" disabled={busy} onClick={() => void play.vetoDecision(false)}>
          Refuse veto
        </Button>
        <Button disabled={busy} onClick={() => void play.vetoDecision(true)}>
          Accept veto
        </Button>
      </div>
    </Panel>
  );
}

function PolicyResult({ game, c }: Ctx) {
  const e = game.lastEnactment!;
  const g = c.t.phases.gridlock;
  const thisTurn = game.log.filter((l) => l.turn === game.turnNumber);
  const unlocked = thisTurn.find((l) => l.type === 'POWER_UNLOCKED');
  const momentum = thisTurn.some((l) => l.type === 'NENSHI_THRESHOLD_REACHED');
  return (
    <Panel title={e.source === 'GRIDLOCK' ? g.title : c.t.phases.policyEnacted.name}>
      {e.source === 'GRIDLOCK' ? <Narrator>{g.narrator}</Narrator> : null}
      <p className="result-line">A {c.policy(e.policy)} was enacted.</p>
      <Narrator>{e.source === 'GRIDLOCK' ? g.automatic : c.t.phases.policyEnacted.narrator}</Narrator>
      {momentum ? (
        <div className="banner">
          <p className="banner-title">{c.t.leader.momentumTitle}</p>
          <p>{c.t.leader.momentumText}</p>
        </div>
      ) : null}
      {unlocked && unlocked.type === 'POWER_UNLOCKED' ? (
        <p>
          {c.factionPlural('FEDERALIST')} unlocked {c.power(unlocked.power)}.
        </p>
      ) : null}
    </Panel>
  );
}

function Power({ view, game, me, priv, busy, c }: Ctx) {
  const power = game.activePower!;
  const minister = game.currentMinisterPlayerId;
  const title = c.power(power);
  const flavour = c.powerNarrator(power);

  if (minister !== me) {
    return (
      <Panel title={title}>
        <Waiting text={`${nameOf(view, minister)} is exercising ${title}.`} />
        <Narrator>{flavour}</Narrator>
      </Panel>
    );
  }

  if (power === ExecutivePower.INTERNAL_POLLING) {
    return (
      <Panel title={title}>
        <Narrator>{flavour}</Narrator>
        <p>Hold to see the next three policies in the deck. Nothing moves.</p>
        <HoldToReveal label={`${title} results`} hint="Hold to view">
          {() => (
            <>
              <p className="reveal-label">Next three policies</p>
              {(priv?.policyPeek ?? []).map((p, i) => (
                <p key={i} className="reveal-name">
                  {i + 1}. {c.policy(p)}
                </p>
              ))}
            </>
          )}
        </HoldToReveal>
        <Button disabled={busy} onClick={() => void play.acknowledgePeek()}>
          Done
        </Button>
      </Panel>
    );
  }

  const researched = new Set(game.log.flatMap((l) => (l.type === 'CAMPAIGN_RESEARCH' ? [l.targetPlayerId] : [])));
  const targets = game.players
    .filter((p) => p.active && p.id !== me && !(power === ExecutivePower.CAMPAIGN_RESEARCH && researched.has(p.id)))
    .map((p) => p.id);
  const action =
    power === ExecutivePower.CAMPAIGN_RESEARCH ? 'Research' : power === ExecutivePower.UNITY_GOVERNMENT ? 'Appoint' : 'Challenge';
  const explain =
    power === ExecutivePower.CAMPAIGN_RESEARCH
      ? 'Choose a player. You will privately learn their faction.'
      : power === ExecutivePower.UNITY_GOVERNMENT
        ? `Choose the next ${c.minister}. Normal rotation resumes afterwards.`
        : 'Choose a player to remove from active play. This cannot be undone.';

  return (
    <Panel title={title}>
      <Narrator>{flavour}</Narrator>
      <p>{explain}</p>
      <PlayerPicker
        view={view}
        ids={targets}
        action={action}
        busy={busy}
        onConfirm={async (id) => {
          await play.selectPlayer(id);
          if (power === ExecutivePower.CAMPAIGN_RESEARCH) {
            showNotice({ kind: 'info', text: 'Research complete. Hold My identity to see the result.' });
          }
        }}
      />
    </Panel>
  );
}
